import CustodyScanForm, { type CustodyFormConfig, type CustodyRow } from './CustodyScanForm';
import { type AppColumn } from '../../components/AppDataGrid';
import { fmtLocalDateTime } from '../../utils/datetime';

// Sample Disposal History — READ-ONLY (ALPACA SampleCustodyTest_DetailView_SampleDisposal_History,
// opened from the Sample Disposal "History" button). Scan a bottle → its FULL custody
// history loads into the grid (one scan → many rows). Same disposal columns.
interface HistRow {
  sampleBottleId: string | null;
  dateDisposed:   string | null;
  disposedByName: string | null;
  deplete:        boolean;
  hazardous:      boolean;
  mode:           string | null;
  wasteDrumId:    string | null;
}

const modeVariant = (m: string) => {
  const s = m.toLowerCase();
  if (s === 'in') return 'ok';
  if (s === 'out') return 'info';
  if (s === 'disposed' || s === 'depleted') return 'alert';
  return 'warn';
};

const COLUMNS: AppColumn<CustodyRow>[] = [
  { field: 'bottle',    header: 'SampleBottleID', width: 170 },
  { field: 'scannedAt', header: 'DateDisposed',   width: 160, text: r => (r.scannedAt ? fmtLocalDateTime(r.scannedAt) : '—') },
  { field: 'by',        header: 'DisposedBy',     width: 130, text: r => r.by || '—' },
  { field: 'deplete',   header: 'Deplete', width: 90, render: r => <input type="checkbox" checked={r.deplete} disabled readOnly />, text: r => (r.deplete ? 'Yes' : 'No') },
  { field: 'haz',       header: 'Hazardous', width: 100, render: r => <input type="checkbox" checked={r.haz} disabled readOnly />, text: r => (r.haz ? 'Yes' : 'No') },
  { field: 'mode',      header: 'Mode', width: 110, render: r => <span className={`bl-pill ${modeVariant(r.mode)}`}>{r.mode}</span>, text: r => r.mode },
  { field: 'wasteDrum', header: 'WasteDrumID',    width: 130, text: r => r.wasteDrum || '—' },
];

const CONFIG: CustodyFormConfig = {
  pageId:         'sample-disposal',   // reuse the Disposal nav icon/colour (no own nav entry)
  titleOverride:  'Sample Disposal History',
  breadcrumbLeaf: 'Sample Disposal History',
  subtitle:       'Scan a bottle barcode to see its full custody history',
  actionLabel:    '',
  confirmTitle:   '',
  confirmMessage: '',
  successVerb:    '',
  formName:       'Sample Disposal',
  readOnly:       true,
  columns:        COLUMNS,
  resolveUrl:     '/api/v1/sample-custody/resolve-history',
  emptyPrompt:    'No data to display — scan a barcode above.',
  // Single-row builder is unused (buildRows overrides), but the type needs it.
  buildRow: (_r, ctx) => ({
    id: `x-${ctx.nowIso}`, bottle: '', from: '', to: '', scannedAt: '', by: '',
    haz: false, mode: '', deplete: false, wasteDrum: '', sampleLoginOid: null, jobOid: null, client: null,
  }),
  buildRows: (r, ctx) => (((r as unknown as { rows?: HistRow[] }).rows) ?? []).map((h, i) => ({
    id:             `${h.sampleBottleId}-${h.mode}-${h.dateDisposed ?? ''}-${ctx.nowIso}-${i}`,
    bottle:         h.sampleBottleId ?? '',
    from:           '',
    to:             '',
    scannedAt:      h.dateDisposed ?? '',
    by:             h.disposedByName ?? '',
    haz:            !!h.hazardous,
    mode:           h.mode ?? '',
    deplete:        !!h.deplete,
    wasteDrum:      h.wasteDrumId ?? '',
    sampleLoginOid: null,
    jobOid:         null,
    client:         null,
  })),
};

export default function SampleDisposalHistoryPage() {
  return <CustodyScanForm config={CONFIG} />;
}
